import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LanguageService } from '../../../../../core/services/language.service';
import { ApiNewsService } from '../../../../../core/services/api/News/api-news';
import { PublicNewsDetail } from '../../../../../core/models/news.model';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ImageViewerComponent } from '../../../../../shared/components/image-viewer/image-viewer.component';
import { environment } from '../../../../../../environments/environment';

@Component({
  selector: 'app-news-detail',
  standalone: true,
  imports: [CommonModule, PssLoadingComponent, ImageViewerComponent],
  templateUrl: './news-detail.component.html',
  styleUrl: './news-detail.component.css',
})
export class NewsDetailComponent implements OnInit, OnDestroy {
  currentLang: 'TH' | 'EN' = 'TH';
  sub!: Subscription;

  detail: PublicNewsDetail | null = null;
  isLoading = false;
  notFound = false;
  viewerImages: string[] = [];
  viewerIndex = 0;
  isViewerOpen = false;

  constructor(
    private languageService: LanguageService,
    private apiNews: ApiNewsService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.sub = this.languageService.currentLang$.subscribe((lang) => {
      this.currentLang = lang;
    });
  }

  /*#################################################### Function ################################################*/
  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id') || 0);
    if (id <= 0) {
      this.notFound = true;
      return;
    }
    this.loadDetail(id);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  goBack(): void {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    this.router.navigate(['/news']);
  }

  /** ปก + รูปในเนื้อหา ตามลำดับที่แสดงบนหน้า */
  get galleryImages(): string[] {
    if (!this.detail) return [];
    const list: string[] = [];
    if (this.detail.coverImageUrl) list.push(this.detail.coverImageUrl);
    for (const img of this.detail.images || []) {
      if (img.fullPath) list.push(img.fullPath);
    }
    return list;
  }

  openZoom(url?: string | null): void {
    if (!url) return;
    const images = this.galleryImages;
    const index = images.indexOf(url);
    this.viewerImages = index >= 0 ? images : [url];
    this.viewerIndex = Math.max(0, index);
    this.isViewerOpen = true;
  }

  closeZoom(): void {
    this.isViewerOpen = false;
  }

  /** "/coupon" หรือ URL ของ LIFF นี้ → เปิดในแอป · URL อื่น → เปิดหน้าใหม่ */
  openLink(): void {
    const url = this.detail?.linkUrl?.trim();
    if (!url) return;
    const internalPath = this.toInternalPath(url);
    if (internalPath) {
      this.router.navigateByUrl(internalPath);
      return;
    }
    window.open(url, '_blank', 'noopener');
  }

  openSitePage(): void {
    const ref = this.detail?.sitePublicRef;
    if (!ref) return;
    this.router.navigate(['/lot', ref]);
  }

  private toInternalPath(url: string): string | null {
    if (url.startsWith('/') && !url.startsWith('//')) return url;
    try {
      const parsed = new URL(url);
      if (parsed.origin === window.location.origin) {
        return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
      }
      const liffPrefix = `/${environment.publicLiffId}`;
      if (parsed.hostname === 'liff.line.me' && parsed.pathname.startsWith(liffPrefix)) {
        const redirect = parsed.searchParams.get('redirect');
        if (redirect) return `/${redirect.replace(/^\/+/, '')}`;
        const rest = parsed.pathname.slice(liffPrefix.length) || '/';
        return `${rest}${parsed.search}${parsed.hash}`;
      }
    } catch {
      return null;
    }
    return null;
  }

  get title(): string {
    if (!this.detail) return '';
    if (this.currentLang === 'EN' && this.detail.titleEn?.trim()) return this.detail.titleEn;
    return this.detail.title;
  }

  get body(): string {
    if (!this.detail) return '';
    if (this.currentLang === 'EN' && this.detail.bodyEn?.trim()) return this.detail.bodyEn;
    return this.detail.body || '';
  }

  formatDate(value?: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  /*############################################# API #################################################*/
  loadDetail(id: number): void {
    this.isLoading = true;
    this.notFound = false;
    this.apiNews.getPublicNewsById(id).subscribe({
      next: (res) => {
        this.detail = res;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('getPublicNewsById failed', err);
        this.detail = null;
        this.notFound = true;
        this.isLoading = false;
      },
    });
  }
}
